import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { resolvePersona, bottomNavForPersona } from "../features/dashboard";

/**
 * Mobile-only bottom navigation bar (sm and smaller).
 *
 * v2.71.39 — REFACTORED to consume the centralized
 * `BOTTOM_NAV_CONFIG` (see `features/dashboard/config/navigationConfig.js`).
 *
 * Before this rev the component embedded a 5-way if-chain hardcoding every
 * persona's tabs inline. Now this file is a pure renderer: persona is
 * resolved once via `resolvePersona(user)` and the bottom-nav config is
 * fetched declaratively via `bottomNavForPersona(persona)`. Adding,
 * removing, or reordering items is a one-line edit to the config.
 *
 * Active-state rules (unchanged from v2.71.37):
 *   • Home tab requires exact `/` match (or full `?focus=…` match).
 *   • Items may declare an explicit `match` prefix to highlight on deep
 *     routes (e.g. /admin/listings/123 → "Listings" stays active).
 *   • `primary` items render as a raised FAB and never display the
 *     active highlight (they're one-shot CTAs).
 *
 * Backward compatibility: legacy `/{role}?tab=…` deep-links continue to
 * work because AdminEntry / DealerEntry / TeamEntry still translate
 * them. Only the bottom-nav itself stopped *producing* those URLs.
 */
export default function MobileBottomNav() {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const nav = useNavigate();

  // Hide on public shortlist share pages or any fullscreen route.
  if (pathname.startsWith("/shortlist/") || pathname === "/magic" || pathname === "/plans-preview" || pathname.startsWith("/office")) {
    return null;
  }

  const persona = resolvePersona(user);
  const tabs = bottomNavForPersona(persona);
  if (!tabs.length) return null;

  /**
   * Resolve active state for a tab.
   *   • Home tab → exact match on `/` (with optional ?focus= preservation).
   *   • Tabs with `match` prefix → `pathname.startsWith(match)`.
   *   • Other tabs → `pathname.startsWith(path)`.
   *   • Query-only differentiation skipped: `primary` flag renders a CTA
   *     button that never carries an active highlight, so two tabs
   *     sharing the same `path` (e.g. /admin/listings vs ?add=1) don't
   *     collide.
   */
  const isActive = (tab) => {
    const [path, qs] = tab.to.split("?");
    if (path === "/") return pathname === "/" && (qs ? search.includes(qs.split("&")[0]) : true);
    const matchPrefix = tab.match || path;
    if (!pathname.startsWith(matchPrefix)) return false;
    if (qs) return search.includes(qs.split("&")[0]);
    return true;
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-[#E4E4E7] shadow-[0_-2px_12px_rgba(9,9,11,0.06)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-5">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = isActive(t);
          if (t.primary) {
            return (
              <button
                key={t.id}
                onClick={() => nav(t.to)}
                className="relative flex items-center justify-center"
                data-testid={t.testid || `mb-${t.id}`}
                aria-label={t.label}
              >
                <span className="absolute -top-4 inline-flex items-center justify-center w-[54px] h-[54px] rounded-full btn-brand shadow-[0_4px_16px_rgba(0,85,255,0.35)] ring-4 ring-white">
                  <Icon size={22} className="text-white" />
                </span>
                <span className="mt-8 text-[10px] font-semibold text-[#0055FF]">{t.label}</span>
              </button>
            );
          }
          return (
            <NavLink
              key={t.id}
              to={t.to}
              end={t.to === "/" || t.to === "/?focus=search"}
              className={`min-h-[56px] flex flex-col items-center justify-center gap-0.5 py-1.5 active:bg-[#F4F4F5] ${
                active ? "text-[#0055FF]" : "text-[#52525B]"
              }`}
              data-testid={t.testid || `mb-${t.id}`}
              aria-label={t.label}
            >
              <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
              <span className={`text-[10px] ${active ? "font-semibold" : "font-medium"}`}>{t.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
